type Listener = () => void

function unSubscribe(listeners: Listener[], listener: Listener) {
  const index = listeners.indexOf(listener)
  if (index === -1) return
  // @ts-expect-error - TODO: fix this
  listeners[index] = listeners[listeners.length - 1]
  listeners.pop()
}

/**
 * Reference: https://github.com/promise-queue/promise-queue/blob/master/lib/index.js
 */
export class PromiseQueue<T> {
  private readonly queue: {
    promiseGenerator: () => Promise<T>
    resolve: (value: T) => void
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    reject: (reason?: any) => void
  }[]

  private isProcessing: boolean
  private readonly addCallbacks: Listener[] = []
  private readonly startCallbacks: Listener[] = []
  private readonly finishCallbacks: Listener[] = []

  constructor() {
    this.queue = []
    this.isProcessing = false
  }

  public add(promiseGenerator: () => Promise<T>) {
    return new Promise((resolve, reject) => {
      this.queue.push({
        promiseGenerator,
        resolve,
        reject,
      })
      this.handleAddCallbacks()

      // Always dequeue first added item.
      if (!this.isProcessing) {
        this.dequeue()
      }
    })
  }

  public getPendingLength() {
    return this.queue.length
  }

  public subscribeAdd(listener: Listener) {
    this.addCallbacks.push(listener)
  }

  public unSubscribeAdd(listener: Listener) {
    unSubscribe(this.addCallbacks, listener)
  }

  public subscribeStart(listener: Listener) {
    this.startCallbacks.push(listener)
  }

  public unSubscribeStart(listener: Listener) {
    unSubscribe(this.startCallbacks, listener)
  }

  public subscribeFinish(listener: Listener) {
    this.finishCallbacks.push(listener)
  }

  public unSubscribeFinish(listener: Listener) {
    unSubscribe(this.finishCallbacks, listener)
  }

  private handleAddCallbacks() {
    this.addCallbacks.forEach(listener => listener())
  }

  private handleStart() {
    this.isProcessing = true
    this.startCallbacks.forEach(listener => listener())
  }

  private handleFinish() {
    this.isProcessing = false
    this.finishCallbacks.forEach(listener => listener())
  }

  private dequeue() {
    const item = this.queue.shift()
    if (!item) return
    try {
      this.handleStart()
      item.promiseGenerator()
        .then(
          (value) => {
            item.resolve(value)
            this.handleFinish()
            this.dequeue()
          },
          (err) => {
            item.reject(err)
            this.handleFinish()
            this.dequeue()
          },
        )
    }
    catch (err) {
      item.reject(err)
      this.handleFinish()
      this.dequeue()
    }
  }
}
